import { useState } from 'react';
import { BarChart3, ChevronRight, Receipt, Sparkles, User, Monitor } from 'lucide-react';
import { Link } from 'react-router';
import { useSession, useSignOut } from '@/hooks/useSession';
import { useSubscription, useTeamMembers } from '@/hooks/useSettings';
import { Screen } from '@m/ui/Screen';
import { Avatar, Group, Row } from '@m/ui/List';
import { ActionSheet } from '@m/ui/Sheet';
import { PLAN_LABEL, openDesktop } from './more/labels';

const icon = 'size-[22px] shrink-0 text-[var(--accent)]';

/** S14. Account, the screens that are not tabs, and sign out. */
export function MoreScreen() {
  const session = useSession();
  const subscription = useSubscription().data;
  const members = useTeamMembers(session.team?.id).data;
  const signOut = useSignOut();
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const plan = subscription ? PLAN_LABEL[subscription.tier] : null;
  const name = session.user.name ?? session.user.email ?? 'You';

  return (
    <Screen title="More">
      <Link
        to="/more/settings"
        className="flex items-center gap-3 rounded-2xl bg-[var(--surface)] px-4 py-3 active:bg-[var(--pressed)]"
      >
        <Avatar id={session.user.id} name={name} size={44} dark />
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-body font-semibold">{name}</span>
          <span className="truncate text-secondary text-[var(--text-muted)]">
            {[session.team?.name, plan && `${plan} plan`].filter(Boolean).join(' · ')}
          </span>
        </span>
        <ChevronRight className="size-4.5 shrink-0 text-[var(--text-subtle)]" strokeWidth={2.4} />
      </Link>

      <Group>
        {session.isAdmin && <Row to="/more/reports" inset={50} leading={<BarChart3 className={icon} />} title="Reports" />}
        <Row to="/more/credits" inset={50} leading={<Receipt className={icon} />} title="Credits & fees" />
        <Row to="/more/personal-hours" inset={50} leading={<User className={icon} />} title="Personal hours" />
      </Group>

      <Group>
        <Row to="/more/team" title="Team" detail={members ? `${members.length} ${members.length === 1 ? 'person' : 'people'}` : undefined} />
        <Row to="/more/subscription" title="Subscription" detail={plan ?? undefined} />
        {session.isAdmin && <Row to="/more/invoice-settings" title="Invoice settings" />}
        <Row to="/more/settings" title="Settings" />
      </Group>

      <Group>
        <Row to="/more/assistant" inset={50} leading={<Sparkles className={icon} />} title="Assistant" />
        <Row onClick={() => openDesktop()} inset={50} leading={<Monitor className={icon} />} title="Open billable.to on desktop" chevron />
      </Group>

      <Group>
        <Row title="Sign out" destructive onClick={() => setConfirmSignOut(true)} />
      </Group>

      <ActionSheet
        open={confirmSignOut}
        onClose={() => setConfirmSignOut(false)}
        title={session.user.email ? `Signed in as ${session.user.email}` : undefined}
        actions={[{ label: 'Sign out', destructive: true, onSelect: () => signOut.mutate() }]}
      />
    </Screen>
  );
}
